import * as React from 'react';
import {
  BankOutlined,
  InfoCircleOutlined,
  ApiOutlined,
  SettingOutlined,
  DashboardOutlined,
  UserOutlined,
  BookOutlined,
  BlockOutlined,
  FileSyncOutlined,
  TeamOutlined,
  PayCircleOutlined
} from '@ant-design/icons';
import {
  UserContextProps,
  AgentContextProps,
  AgentContext,
  UserContext,
  OrgContext,
  OrgContextProps
} from '@interface/index';
import { Link } from 'react-router-dom';
import { localizedString } from '@locale/index';
import {
  AgentPermission,
  AGENT_OPERATION
} from '@permission/index';
import {
  getOrgId,
  getAgentId,
  getAgentPathPrefix,
  isBotAgent,
  getOrgName,
  PathKeys,
  PathKeyRegxMap,
  isPostgresProviderAgent,
  isBuildPage,
  isOperatePage,
  isDebugPage,
  isOrgOwner,
  isHostedProviderAgent
} from '@lib/index';
import { Menu, MenuProps } from 'antd';
import { throttle } from 'lodash';
import { AGENT_MODE } from '@config/constants';

export interface INavTabProps {
  openKeys: string[];
  collapsed: boolean;
  history: any;
  $user: UserContextProps;
  $org: OrgContextProps;
  $agent: AgentContextProps;
}

export interface INavTabState {
  selectedKeys: string[];
  widthKey: string;
}

class NavTabComp extends React.Component<INavTabProps, INavTabState> {
  private mounted = false;
  private cleanupFns: (() => void)[] = [];
  private resizeObserver: ResizeObserver | null = null;
  private menuRef: React.RefObject<HTMLDivElement> = React.createRef();
  state: INavTabState = {
    selectedKeys: [],
    widthKey: ''
  };
  unListen: any;

  componentDidMount() {
    this.mounted = true;

    // 使用 ResizeObserver 替代 window resize 事件
    this.resizeObserver = new ResizeObserver(this.updateKey);
    if (this.menuRef.current) {
      this.resizeObserver.observe(this.menuRef.current);
    }

    const listenSelectedKeys = () => {
      if (this.mounted && location.pathname) {
        this.selectKey(location.pathname);
      }
    };

    listenSelectedKeys();

    this.unListen = this.props.history.listen(() => {
      listenSelectedKeys();
    });

    // 添加清理函数
    this.cleanupFns.push(() => {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.unListen) {
        this.unListen();
        this.unListen = null;
      }
    });
  }

  componentDidUpdate(prevProps: INavTabProps) {
    // 当路由变化时更新选中的键
    if (prevProps.history.location.pathname !== this.props.history.location.pathname) {
      this.selectKey(this.props.history.location.pathname);
    }
  }

  updateKey = (entries: ResizeObserverEntry[]) => {
    if (!this.mounted) return;

    const entry = entries[0];
    if (!entry) return;

    const widthKey = entry.contentRect.width.toString();
    if (widthKey === this.state.widthKey) {
      return;
    }

    this.safeSetState({ widthKey });
  };

  componentWillUnmount() {
    this.mounted = false;
    // 执行所有清理函数
    this.cleanupFns.forEach(cleanup => cleanup());
  }

  // 添加一个安全的 setState 方法
  safeSetState = <K extends keyof INavTabState>(
    state: ((prevState: Readonly<INavTabState>) => Pick<INavTabState, K> | INavTabState | null) | Pick<INavTabState, K> | INavTabState | null,
    callback?: () => void
  ): void => {
    if (this.mounted) {
      this.setState(state, callback);
    }
  };

  /**
   * 根据当前各条件构造 Menu 的 items 数组
   */
  getMenuItems = (): MenuProps['items'] => {
    const { $agent, $user, $org } = this.props;
    const items: MenuProps['items'] = [];

    // 当为 agent 场景
    if (getAgentId()) {
      // 以下部分与 dialogInteractionComp() 中的逻辑一致
      if (isBuildPage()) {
        if ($agent.agent && ($agent.agent.isService || $agent.agent.isServiceImplementation)) {
          items.push({
            key: PathKeys.ServiceSchema,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/service_schema`}>
                <ApiOutlined />
                <span>{localizedString('sidebar_service_schema')}</span>
              </Link>
            )
          });
        }
        if ($agent.agent && $agent.agent.mode === AGENT_MODE.KNOWLEDGE) {
          // items.push({
          //   key: PathKeys.KNOWLEDGE_SYSTEM_PROMPT,
          //   className: 'bold',
          //   label: (
          //     <Link to={`${getAgentPathPrefix()}/knowledge_system_prompt`}>
          //       <BlockOutlined />
          //       <span>System prompt</span>
          //     </Link>
          //   )
          // });
        } else {
          items.push({
            key: PathKeys.FAKE_TYPES,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/type`}>
                <BlockOutlined />
                <span>{localizedString('fake_types')}</span>
              </Link>
            )
          });
        }
        if ($agent.agent && $agent.agent.mode !== AGENT_MODE.KNOWLEDGE) {
          items.push({
            key: PathKeys.Libraries,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/library`}>
                <FileSyncOutlined />
                <span>{localizedString('agent_libraries')}</span>
              </Link>
            )
          });
        } else {
          items.push({
            key: PathKeys.KNOWLEDGES,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/knowledge`}>
                <BlockOutlined />
                <span>{localizedString('knowledge_collections')}</span>
              </Link>
            )
          });
        }
        // 添加分割线
        items.push({
          type: 'divider',
          key: 'interaction-divider'
        });
      }

      // 以下部分判断 AgentPermission(READ_AGENT_INFO)
      if (AgentPermission(AGENT_OPERATION.READ_AGENT_INFO)) {
        if ($agent.agent && $agent.agent.mode !== AGENT_MODE.KNOWLEDGE && isBuildPage()) {
          items.push({
            key: PathKeys.Version,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/version`}>
                <InfoCircleOutlined />
                <span>{localizedString('sidebar_action_log')}</span>
              </Link>
            )
          });
        }
        if (isBuildPage()) {
          items.push({
            key: PathKeys.AgentSetting,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/setting?tab=general`}>
                <SettingOutlined />
                <span>{localizedString('sidebar_agent_config')}</span>
              </Link>
            )
          });
        }
        if (isDebugPage()) {
          items.push({
            key: PathKeys.AgentDebug,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/debug`}>
                <DashboardOutlined />
                <span>Debug</span>
              </Link>
            )
          });
          items.push({
            key: PathKeys.DebugSetting,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/debug_setting?tab=integration`}>
                <SettingOutlined />
                <span>{localizedString('integration')}</span>
              </Link>
            )
          });
        }
        if ((isBotAgent() || isHostedProviderAgent()) && isOperatePage()) {
          items.push({
            key: PathKeys.AgentDeploy,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/deploy`}>
                <DashboardOutlined />
                <span>Deploy</span>
              </Link>
            )
          });
        }
        if (isOperatePage() && (isHostedProviderAgent() || isBotAgent())) {
          items.push({
            key: PathKeys.OperateSetting,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/operate_setting?tab=integration`}>
                <SettingOutlined />
                <span>{localizedString('integration')}</span>
              </Link>
            )
          });
        }

        if (isOperatePage() && isBotAgent()) {
          items.push({
            key: PathKeys.Outbound,
            className: 'bold',
            label: (
              <Link to={`${getAgentPathPrefix()}/outbound`}>
                <DashboardOutlined />
                <span>Outbound</span>
              </Link>
            )
          });
        }
      }
    }

    // 当为组织场景且非 agent 场景时
    if (getOrgId() && !getAgentId()) {
      items.push({
        key: PathKeys.Projects,
        className: 'bold',
        label: (
          <Link to={`/org/${getOrgName()}/agent`}>
            <BookOutlined />
            <span>{localizedString('projects')}</span>
          </Link>
        )
      });
      items.push({
        key: PathKeys.OrgMembers,
        className: 'bold',
        label: (
          <Link to={`/org/${getOrgName()}/member`}>
            <TeamOutlined />
            <span>{localizedString('team_members')}</span>
          </Link>
        )
      });
      items.push({
        key: PathKeys.OrgSetting,
        className: 'bold',
        label: (
          <Link to={`/org/${getOrgName()}`}>
            <SettingOutlined />
            <span>{localizedString('org_setting')}</span>
          </Link>
        )
      });
      if (isOrgOwner()) {
        items.push({
          key: PathKeys.OrgSettingBilling,
          className: 'bold',
          label: (
            <Link to={`/org/${getOrgName()}/billing`}>
              <PayCircleOutlined />
              <span>Billing</span>
            </Link>
          )
        });
      }
    }

    // 当用户存在但不属于任何组织时
    if ($user.user && !$org.org) {
      items.push({
        key: PathKeys.Account,
        className: 'bold',
        label: (
          <Link to="/account">
            <UserOutlined />
            <span>{localizedString('user_profile')}</span>
          </Link>
        )
      });
      items.push({
        key: PathKeys.OrgList,
        className: 'bold',
        label: (
          <Link to="/org">
            <BankOutlined />
            <span>{localizedString('sidebar_org')}</span>
          </Link>
        )
      });
    }

    return items;
  };

  render() {
    const { collapsed } = this.props;
    const { selectedKeys, widthKey } = this.state;
    const menuItems = this.getMenuItems();

    // 确保只在组件挂载时渲染 Menu
    if (!this.mounted) {
      return null;
    }

    return (
      <div ref={this.menuRef} className="tab-menu-container" style={{ flex: 1 }}>
        <Menu
          key={widthKey}
          className="tab-menu"
          theme="light"
          style={{ flex: 1 }}
          selectedKeys={selectedKeys}
          mode="horizontal"
          items={menuItems}
        />
      </div>
    );
  }

  selectKey = (path: string) => {
    if (!this.mounted) return;

    if (path.endsWith('provider_connection')) {
      this.safeSetState({ selectedKeys: [PathKeys.AgentSetting] });
      return;
    }

    for (const [k, v] of Object.entries(PathKeyRegxMap)) {
      if (Array.isArray(v)) {
        for (const regex of v) {
          if (this.matchPath(path, regex)) {
            this.safeSetState({
              selectedKeys: [k]
            });
            return;
          }
        }
      } else {
        if (this.matchPath(path, v)) {
          this.safeSetState({
            selectedKeys: [k]
          });
          break;
        }
      }
    }
  };

  matchPath = (path: string, regex: RegExp) => {
    const ret = path.match(regex);
    return ret && ret.length && ret[0] === path;
  };
}

export const NavTab = (props: any) => (
  <AgentContext.Consumer>
    {($agent: AgentContextProps) => (
      <OrgContext.Consumer>
        {($org: OrgContextProps) =>
          <UserContext.Consumer>
            {($user: UserContextProps) => (
              <NavTabComp {...props} $user={$user} $org={$org} $agent={$agent} />
            )}
          </UserContext.Consumer>}
      </OrgContext.Consumer>
    )}
  </AgentContext.Consumer>
);
